import { addDevice, clearAddDeviceData } from "./deviceSetupSlice";
import { Action } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { useEffect, useRef, useState } from "react";
import React from "react";
import { useSpinner } from "../../customHooks";
import { DefaultTopLevelGroup } from "../../common/constants";
import {
  selector as groupApi,
  groupList,
} from "../deviceManagement/groupSlice";
import { Status } from "../../common/constants";
import { IGroup } from "../../common/types/groupTypes";
import {
  IGroupAlerts,
  ISelectGroupState,
} from "../../common/types/alertsTypes";
import useNotification from "../../components/notification/useNotification";
import { getAllValuesWithLabels, isSuccessResponse } from "../../common/utils";
import { AxiosError } from "axios";
import { ISuccsessResponse } from "../../common/types/uiTypes";
import {
  IDevice,
  IDevicePayload,
  IDeviceSetupState,
} from "../../common/types/devicesTypes";
import { ITreeNode } from "../../common/types/commonTypes";
import { getAllNodeValues } from "../../components/treeView/util";

const GROUP_NOT_FOUND: string = "Group not found";
const GROUP_LOADING: string = "Group is loading";

interface IAddDeviceResponseState {
  deviceSetup: IDeviceSetupState;
}

interface IAddedDevice extends IDevice {
  groupName: string;
}

export const useDeviceSetup = () => {
  const dispatch: ThunkDispatch<IAddDeviceResponseState, undefined, Action> =
    useDispatch();

  const createSelectGroupState: ISelectGroupState = {
    groups: [],
    checked: [],
    selectAllChecked: false,
  };
  const addDeviceResponse = useSelector(
    (state: IAddDeviceResponseState) => state.deviceSetup
  );
  const [deviceId, setDeviceId] = useState<string>("");
  const [deviceIdFlag, setDeviceIdFlag] = useState<boolean>(false);
  const [group, setGroup] = useState<string | null>("");
  const [deviceCode, setDeviceCode] = useState<string>("");
  const [alertMessage, setAlertMessage] = useState<string | null>();
  const [selectGroupState, setSelectGroupState] = useState<ISelectGroupState>(
    createSelectGroupState
  );
  const [selectedGroups, setSelectedGroups] = useState<IGroupAlerts[]>(
    selectGroupState?.groups
  );
  const groups: IGroup[] = useSelector(groupApi).groups;
  const groupsStatus: string = useSelector(groupApi).status;
  const addDeviceStatus: string = addDeviceResponse.status;
  const status: string =
    groupsStatus === Status.PENDING ? groupsStatus : addDeviceStatus;
  const [refresh, setRefresh] = useState<boolean>(false);
  const [inputErrors, setInputErrors] = useState({
    deviceId: "",
    deviceCode: "",
  });
  if (groupsStatus == Status.IDLE) {
    dispatch(groupList());
  }
  useSpinner(status);

  const findGroupNameById = (value: string): string => {
    for (const item of groups) {
      if (item.orgId === value) {
        return item.displayName;
      }
    }
    if (groups.length == 0) {
      return GROUP_LOADING;
    }
    return GROUP_NOT_FOUND;
  };
  const flatData: IAddedDevice[] = addDeviceResponse?.devices.flatMap((row) =>
    row.organizations.map((org) => ({
      ...row,
      groupName: findGroupNameById(org),
    }))
  );
  const [addedDevices, setAddedDevices] = useState<IAddedDevice[]>(
    addDeviceResponse?.devices ? flatData : []
  );

  const handleDataFromTreeView = (selectedGroup) => {
    const groups: IGroupAlerts[] = getAllValuesWithLabels(
      selectedGroup,
      selectGroupState
    );
    setSelectedGroups(groups);
    const allNodeValues: string[] = getAllNodeValues(
      selectedGroup,
      selectGroupState.selectAllChecked
    );
    setSelectGroupState({
      ...selectGroupState,
      groups: groups,
      checked: allNodeValues,
    });
  };

  const handleDeviceIdInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const invalidCharacters: boolean = /[^A-Z0-9]/g.test(event.target.value);
    setDeviceIdFlag(invalidCharacters);
    setDeviceId(event.target.value.replace(/[^A-Z0-9]/g, ""));

    if (inputErrors.deviceId) {
      setInputErrors((prev) => ({
        ...prev,
        deviceId: "",
      }));
    }
  };

  const handleDeviceCodeInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDeviceCode(event.target.value);

    if (inputErrors.deviceCode) {
      setInputErrors((prev) => ({
        ...prev,
        deviceCode: "",
      }));
    }
  };

  const handleCloseAlert = () => {
    setAlertMessage(null);
  };

  const { showSuccessNotification, showWarningNotification } =
    useNotification();

  const handleAddButtonClick = () => {
    setAlertMessage(null);
    setInputErrors({ deviceId: "", deviceCode: "" });

    let hasError = false;
    const newErrors = { deviceId: "", deviceCode: "" };

    if (!deviceId || deviceId.trim() === "") {
      newErrors.deviceId = "Device ID is required.";
      hasError = true;
    }

    if (!deviceCode || deviceCode.trim() === "") {
      newErrors.deviceCode = "Device Code is required.";
      hasError = true;
    }

    if (hasError) {
      setInputErrors(newErrors);
      return;
    }

    const payload: IDevicePayload = {
      deviceId: deviceId,
      deviceCode: deviceCode,
    };
    if (selectedGroups.length > 0) {
      payload.organizationId = selectedGroups.reduce(
        (selectedGroup, currentGroup) => {
          return [...selectedGroup, currentGroup.id];
        },
        []
      );
    } else {
      payload.organizationId = [DefaultTopLevelGroup.ID];
    }

    dispatch(addDevice(payload)).then((response) => {
      if (response.meta.requestStatus === "fulfilled") {
        const successData = response.payload as ISuccsessResponse;

        if (isSuccessResponse(successData)) {
          showSuccessNotification(successData.message);
          setRefresh(true);
          setSelectGroupState(createSelectGroupState);
          setDeviceId("");
          setDeviceCode("");
        }
      } else {
        const errorData = response.payload as AxiosError<any>;

        const errorMessage =
          errorData?.response?.data?.message ||
          errorData?.message ||
          "Unable to connect. Please check device status.";

        showWarningNotification(errorMessage);
      }
    });
  };

  useEffect(() => {
    setAlertMessage(addDeviceResponse.msg);
    setAddedDevices(flatData);
  }, [addDeviceResponse]);

  useEffect(() => {
    return () => {
      dispatch(clearAddDeviceData());
    };
  }, [dispatch, clearAddDeviceData]);

  return {
    deviceId,
    deviceCode,
    alertMessage,
    addDeviceResponse,
    handleDeviceIdInputChange,
    handleDeviceCodeInputChange,
    handleCloseAlert,
    handleAddButtonClick,
    setAlertMessage,
    handleDataFromTreeView,
    findGroupNameById,
    selectGroupState,
    setSelectGroupState,
    groupsStatus,
    status,
    addDeviceStatus,
    refresh,
    setRefresh,
    addedDevices,
    deviceIdFlag,
    inputErrors,
  };
};
